import cx from 'classnames';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import partition from 'lodash/partition';
import uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Card, Icon, Label } from 'semantic-ui-react';

import { userAgenda } from '../../agenda/store/agenda.selectors';
import Images from '../../utils/Images';
import { checkUserAvailability } from '../../utils/agendaUtils';
import { useItemValueColor } from '../../utils/designUtils';
import Tag from '../../workshops/components/Tag';
import { getClassName, getWorkshopString } from '../../workshops/utils';
import UserAvatars from '../Avatars/UserAvatars';
import EntityLink from '../EntityLink';
import { EntityTags } from '../EntityTags';
import QuotaField from './QuotaField';
import './WorkshopCard3.scss';
import WorkshopDate from './WorkshopDate';

const translationPrefix = 'workshops.card';

const WorkshopCard3 = ({
  workshop,
  hasSessions,
  showSpeakers,
  showExhibitors,
  showDuration,
  showEndTime,
  speakersConfig,
  exhibitorsConfig,
  tagFields,
  ...rest
}) => {
  const {
    _id,
    endDate,
    exhibitors,
    image,
    productCategories,
    quota,
    shortDescription,
    speakers = [],
    startDate,
    technicityLevel,
    title,
    location,
    usersCount,
    workshopStatus,
  } = workshop;
  const { dateConfig } = rest;
  const { color: categoryColor, style: categoryStyle } = useItemValueColor(
    'workshops',
    workshop,
    'category',
  );
  const { t } = useTranslation();

  const hasImage = image && image.uri;
  const agenda = useSelector(userAgenda);
  const { hasOverlappingEvent, isRegistered } = useMemo(() => {
    const [registeredEvent, otherEvents] = partition(agenda, (evt) => evt._id === _id);
    return {
      hasOverlappingEvent: !checkUserAvailability(otherEvents, workshop),
      isRegistered: registeredEvent.length > 0,
    };
  }, [agenda, workshop, _id]);
  const { extraFields = [] } = speakersConfig;
  const extraSpeakers = useMemo(
    () => flatten(extraFields.map((field) => get(workshop, field, []))),
    [extraFields, workshop],
  );

  return (
    <div
      className={cx(
        'workshop--card3',
        getClassName(workshop),
        `session--${workshopStatus}`,
        hasOverlappingEvent && `session--has-overlapping-event`,
        isRegistered && `session--is-registered`,
        { 'no-image': !hasImage },
      )}
      style={{ '--ac-color-category': categoryColor }}
    >
      <Card link as={EntityLink} entity={workshop}>
        {hasImage && (
          <div className="card__image">
            <div
              className="image-with-shadow"
              style={{
                backgroundImage: `url(${Images.maxWidth(image, 800)})`,
              }}
            />
          </div>
        )}
        {hasImage && <Card.Content style={{ marginTop: 'calc(56.25% - 70px)', borderTop: 0 }} />}
        <Card.Content className="borderless">
          <p
            className={cx('workshop--category', { hasColor: !!categoryColor })}
            style={categoryStyle}
          >
            {getWorkshopString(workshop, 'category')}
          </p>
          <Card.Description style={{ marginTop: 8, marginBottom: 8 }}>
            <WorkshopDate
              startDate={startDate}
              endDate={endDate}
              showDuration={showDuration}
              showEndTime={showEndTime}
              dateConfig={dateConfig}
            />
            {location && (
              <div className="location">
                <Icon name="map marker alternate" />
                {location}
              </div>
            )}
          </Card.Description>
          <QuotaField quota={quota} usersCount={usersCount} />
          <Card.Header style={{ fontSize: '1.2em', fontWeight: 'normal' }}>{title}</Card.Header>
          {showSpeakers && (
            <UserAvatars
              collection="speakers"
              userIds={uniq([...speakers, ...extraSpeakers])}
              {...speakersConfig}
            />
          )}
          {showExhibitors && (
            <UserAvatars
              collection="exhibitors"
              imageKey="logo"
              userIds={exhibitors}
              {...exhibitorsConfig}
            />
          )}
          {tagFields?.length > 0 && (
            <EntityTags tagFields={tagFields} entity={workshop} type="workshop" />
          )}
          {/* Rename Show Exhibitors */}
          <div className="tags">
            {technicityLevel && <Label size="small">{technicityLevel}</Label>}
            {productCategories &&
              productCategories.length > 0 &&
              productCategories.map((pc) => (
                <Tag
                  key={pc}
                  size="small"
                  value={pc}
                  translationPrefix="workshop.tag.productCategories"
                />
              ))}
          </div>
          {/* <p style={{ lineHeight: '1.25', color: 'rgba(0, 0, 0, 0.5)', marginTop: 8 }}>
              Technicity Level : {technicityLevel}
            </p> */}
          <Card.Description className="shortDescription">{shortDescription}</Card.Description>
          {!hasSessions && (
            <div className="card__read-more">
              {t(`${translationPrefix}.read-more`, {
                context: isRegistered ? 'registered' : undefined,
              })}
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

WorkshopCard3.defaultProps = {
  hasSessions: false,
  showSpeakers: false,
  showExhibitors: false,
  showDuration: false,
  showEndTime: false,
  speakersConfig: {},
  exhibitorsConfig: {},
};

WorkshopCard3.propTypes = {
  hasSessions: PropTypes.bool,
  showSpeakers: PropTypes.bool,
  showExhibitors: PropTypes.bool,
  showDuration: PropTypes.bool,
  showEndTime: PropTypes.bool,
  speakersConfig: PropTypes.object,
  exhibitorsConfig: PropTypes.object,
  workshop: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    category: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string.isRequired,
    ]),
    collection: PropTypes.string.isRequired,
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    exhibitors: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    image: PropTypes.object,
    productCategories: PropTypes.array,
    location: PropTypes.string,
    quota: PropTypes.number,
    technicityLevel: PropTypes.string,
    title: PropTypes.string.isRequired,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    shortDescription: PropTypes.string,
    slug: PropTypes.string,
    speakers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    usersCount: PropTypes.number,
    workshopStatus: PropTypes.string,
  }).isRequired,
};

export default WorkshopCard3;
